import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useSocialJoinStore from '@/stores/common/socialJoin';
import useUserStore from '@/stores/user/user';
import RoleSelectionModal from '@/components/RoleSelectionModal';
import NewModal from '@/components/NewModal';
import {
    ContainerFit,
    ContainerPadding,
    Fz18Bold,
    Fz15,
    Fz15Bold,
    Mb10,
    Mb20,
    Mb30,
    Mb40,
    Flxbt,
    FlxMiddle,
    JoinField,
    JoinInput,
    InputWrapper,
    BtnInInputRound,
    FieldSet,
    PadVert10,
    fz14Bold,
    ColAccent2,
    BtnFullModal,
    Pad20,
    PopShortMsg,
    Paragraph,
    ColAccent,
    ListCol2,
    BtnHalfModalBlack,
    BtnHalfModal,
    FlxbtFullMb20,
    Fz18Medium,
    ListColumn,
    PadVert30PopOver,
    TermOver,
    NavHeight,
    BtnCloseModal,
    StickyBtm,
    LineCenter,
    AbsolBtm,
    Spacing10,
    BtnCloseModal3,
} from '@/css/style';
import { useShopQuery } from '@/services/shopService';
import { useCompleteSocialRegistrationMutation } from '@/services/authService';
import { useIntersectionObserver } from '@/utils/helper';
import RadioBox from '@/components/RadioBox';
import NoContent from '@/components/NoContent';
import arrowLeft from '@/assets/img/arrow_left_thin_bl.svg';
import arrowDownThinGray from '@/assets/img/arrow_down_thin_gray.svg';
import icCloseModal from '@/assets/img/ic_close_modal_gray.svg';

function JoinSocial() {
    const navigate = useNavigate();
    const location = useLocation();
    const sjs = useSocialJoinStore();
    const us = useUserStore();
    const containerRef = useRef(null);

    const socialInfo = location.state?.socialInfo || sjs.socialInfo;

    const [isLoading, setIsLoading] = useState(false);
    const [errorModal, setErrorModal] = useState({ show: false, message: '' });

    const completeSocialRegistration = useCompleteSocialRegistrationMutation(
        us,
        (res) => {
            setIsLoading(false);
            sjs.reset();
            navigate('/', { replace: true });
        },
        (error) => {
            setIsLoading(false);
            console.error('Registration failed:', error);
            const errorMessage = error?.response?.data?.message || '회원가입 처리 중 오류가 발생했습니다.';
            setErrorModal({ show: true, message: errorMessage });
        }
    );

    const [shopSearchName, setShopSearchName] = React.useState('');
    const [selectedShop, setSelectedShop] = React.useState(null);
    const [showShopSearch, setShowShopSearch] = React.useState(false);

    const {
        data: shopData,
        refetch: shopRefetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useShopQuery({ name: shopSearchName });

    const observer = useIntersectionObserver(
        fetchNextPage,
        containerRef,
        shopData,
        isFetchingNextPage
    );

    useEffect(() => {
        if (!socialInfo) {
            navigate('/login', { replace: true });
            return;
        }
        sjs.setSocialLoginResult({
            isNewUser: true,
            needsRoleSelection: true,
            socialInfo: socialInfo,
        });
    }, []);

    const handleRoleSelect = (role, info) => {
        console.log('[JoinSocial] handleRoleSelect called with role:', role);
        console.log('[JoinSocial] socialInfo:', info);
        
        if (!info || !info.provider || !info.social_id) {
            console.error('[JoinSocial] Missing required socialInfo fields:', { 
                provider: info?.provider, 
                social_id: info?.social_id 
            });
            setErrorModal({ 
                show: true, 
                message: '소셜 로그인 정보가 올바르지 않습니다. 다시 시도해 주세요.' 
            });
            return;
        }

        sjs.setSelectedRole(role);
        
        if (role === 'user') {
            setIsLoading(true);
            completeSocialRegistration.mutate({
                role: 'user',
                provider: info.provider,
                social_id: info.social_id,
                email: info.email || '',
                name: info.name || '',
                nickname: info.nickname || info.name || '',
            });
        }
    };

    const handleShopSelect = () => {
        if (!selectedShop) return;
        
        const info = sjs.socialInfo;
        if (!info || !info.provider || !info.social_id) {
            setErrorModal({ 
                show: true, 
                message: '소셜 로그인 정보가 올바르지 않습니다. 다시 시도해 주세요.' 
            });
            return;
        }

        setIsLoading(true);
        completeSocialRegistration.mutate({
            role: 'designer',
            provider: info.provider,
            social_id: info.social_id,
            email: info.email || '',
            name: info.name || '',
            nickname: info.nickname || info.name || '',
            shop_id: selectedShop.id,
        });
    };

    const handleClose = () => {
        sjs.reset();
        navigate('/login', { replace: true });
    };

    return (
        <>
            <RoleSelectionModal
                isOpen={sjs.showRoleModal}
                onClose={handleClose}
                onSelectRole={handleRoleSelect}
                socialInfo={socialInfo}
            />

            <TermOver $visible={sjs.showShopSelection}>
                <ContainerPadding>
                    <BtnCloseModal onClick={() => sjs.closeShopSelection()}>
                        <img src={arrowLeft} alt="뒤로" />
                    </BtnCloseModal>
                    <NavHeight />
                    <Spacing10 />
                    <Mb40>
                        <Fz18Bold>근무하시는 헤어샵을 선택해 주세요</Fz18Bold>
                    </Mb40>
                    <JoinField>
                        <Mb10>
                            <InputWrapper>
                                <JoinInput
                                    type="text"
                                    placeholder="헤어샵 이름을 입력 후 검색해 주세요"
                                    maxLength="20"
                                    value={shopSearchName}
                                    onChange={(e) => setShopSearchName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && shopSearchName) {
                                            shopRefetch();
                                            setShowShopSearch(true);
                                        }
                                    }}
                                />
                                <BtnInInputRound
                                    onClick={() => {
                                        if (shopSearchName) {
                                            shopRefetch();
                                            setShowShopSearch(true);
                                        }
                                    }}
                                >
                                    검색
                                </BtnInInputRound>
                            </InputWrapper>
                        </Mb10>
                        <Paragraph $fz="1.154rem" $col="#A1A1A1">
                            디자이너 님이 활동하시는 헤어샵 이름으로 검색해 주세요!
                            <br />( 근무 헤어샵은 매니저 승인 후 등록 가능합니다. )
                        </Paragraph>
                    </JoinField>

                    {showShopSearch && (
                        <PadVert30PopOver>
                            <ListColumn>
                                {shopData?.pages.map((page) =>
                                    page.data.docs.map((item) => (
                                        <FlxbtFullMb20 key={item.id}>
                                            <label htmlFor={item.id}>
                                                <Fz18Medium $mb="1.538rem">{item.name}</Fz18Medium>
                                                <Fz15 $col="#a1a1a1">{item.address}</Fz15>
                                            </label>
                                            <RadioBox
                                                name="salon"
                                                checked={selectedShop?.id === item.id}
                                                onChange={() => setSelectedShop(item)}
                                                id={item.id}
                                                value={item}
                                            />
                                        </FlxbtFullMb20>
                                    ))
                                )}
                                {!(shopData?.pages && shopData?.pages[0].data.docs.length > 0) && (
                                    <NoContent message="검색된 헤어샵이 없습니다." />
                                )}
                            </ListColumn>

                            {hasNextPage && (
                                <LineCenter ref={containerRef}>
                                    <Fz15Bold $col="#A1A1A1">더보기</Fz15Bold>
                                    <img src={arrowDownThinGray} alt="more" />
                                </LineCenter>
                            )}
                        </PadVert30PopOver>
                    )}
                </ContainerPadding>
                <StickyBtm>
                    <BtnFullModal $active={!!selectedShop} onClick={handleShopSelect}>
                        가입완료
                    </BtnFullModal>
                </StickyBtm>
            </TermOver>

            <NewModal isOpen={isLoading} contentLabel="로딩 중" ariaHideApp={false}>
                <Pad20>
                    <PopShortMsg>
                        <Mb20>
                            <Fz15Bold $col="#000">회원가입 처리 중...</Fz15Bold>
                        </Mb20>
                        <Paragraph $fz="1.154rem">잠시만 기다려 주세요.</Paragraph>
                    </PopShortMsg>
                </Pad20>
            </NewModal>

            <NewModal isOpen={errorModal.show} contentLabel="오류" ariaHideApp={false}>
                <BtnCloseModal3>
                    <img 
                        src={icCloseModal} 
                        alt="닫기" 
                        onClick={() => setErrorModal({ show: false, message: '' })} 
                    />
                </BtnCloseModal3>
                <Pad20>
                    <PopShortMsg>
                        <Mb20>
                            <Fz15Bold $col="#000">회원가입 오류</Fz15Bold>
                        </Mb20>
                        <Paragraph $fz="1.154rem">{errorModal.message}</Paragraph>
                    </PopShortMsg>
                    <BtnFullModal 
                        $active 
                        onClick={() => {
                            setErrorModal({ show: false, message: '' });
                            navigate('/login', { replace: true });
                        }}
                    >
                        로그인으로 돌아가기
                    </BtnFullModal>
                </Pad20>
            </NewModal>
        </>
    );
}

export default JoinSocial;
